/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MessageStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface MessageStatusUpdateProps {
  id: string;
  status: MessageStatus;
  error: string | null;
  vendorMessage: string | null;
  metadataJson: string | null;
  trackedMessageId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class MessageStatusUpdateRecord extends Record<MessageStatusUpdateProps>({
  id: null as any,
  status: null as any,
  error: null as any,
  vendorMessage: null as any,
  metadataJson: null as any,
  trackedMessageId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: MessageStatus;
    error: string | null;
    vendorMessage: string | null;
    metadataJson: string | null;
    trackedMessageId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): MessageStatusUpdateRecord {
    if (json == null) {
      throw new Error('MessageStatusUpdateRecord.fromJS: parameter cannot be null');
    }
    return new MessageStatusUpdateRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'MessageStatusUpdateJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', MessageStatus.Created),
        'MessageStatusUpdateJson.status',
      ),
      error: json.error,
      vendorMessage: json.vendorMessage,
      metadataJson: json.metadataJson,
      trackedMessageId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'trackedMessageId', '00000000-0000-0000-0000-000000000000'),
        'MessageStatusUpdateJson.trackedMessageId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: MessageStatus;
    error: string | null;
    vendorMessage: string | null;
    metadataJson: string | null;
    trackedMessageId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [MessageStatusUpdateRecord, Error[]] {
    if (json == null) {
      throw new Error('MessageStatusUpdateRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new MessageStatusUpdateRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'MessageStatusUpdateJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', MessageStatus.Created),
            'MessageStatusUpdateJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      error: tryOrDefault(
        () => json.error,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorMessage: tryOrDefault(
        () => json.vendorMessage,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      metadataJson: tryOrDefault(
        () => json.metadataJson,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      trackedMessageId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'trackedMessageId', '00000000-0000-0000-0000-000000000000'),
            'MessageStatusUpdateJson.trackedMessageId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: MessageStatusUpdateProps) {
    super(props);
    if (props == null) {
      throw new Error('MessageStatusUpdateRecord constructor parameter cannot be null');
    }
  }
}

export abstract class MessageStatusUpdateViewModelBase implements MessageStatusUpdateProps {
  protected readonly _props: MessageStatusUpdateProps;
  private _json?: MessageStatusUpdateProps;

  get id(): string {
    return this._props.id;
  }
  get status(): MessageStatus {
    return this._props.status;
  }
  get error(): string | null {
    return this._props.error;
  }
  get vendorMessage(): string | null {
    return this._props.vendorMessage;
  }
  get metadataJson(): string | null {
    return this._props.metadataJson;
  }
  get trackedMessageId(): string {
    return this._props.trackedMessageId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCreated() {
    return this._props.status === MessageStatus.Created;
  }
  public get isFailed() {
    return this._props.status === MessageStatus.Failed;
  }
  public get isReceivedByRecipient() {
    return this._props.status === MessageStatus.ReceivedByRecipient;
  }
  public get isSentToProvider() {
    return this._props.status === MessageStatus.SentToProvider;
  }
  public get isSentToRecipient() {
    return this._props.status === MessageStatus.SentToRecipient;
  }

  public isAnyStatus(...messageStatus: MessageStatus[]) {
    return messageStatus.includes(this._props.status);
  }

  constructor(props: MessageStatusUpdateProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        error: this._props.error,
        vendorMessage: this._props.vendorMessage,
        metadataJson: this._props.metadataJson,
        trackedMessageId: this._props.trackedMessageId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllMessageStatusUpdateEntities = (state: AppState) =>
  state.entityStores.messageStatusUpdate.entities;

export const selectMessageStatusUpdateById = (id: string) => (state: AppState) =>
  state.entityStores.messageStatusUpdate.entities.get(id);

export const selectMessageStatusUpdateMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.messageStatusUpdate.meta.get(id);

export const selectMessageStatusUpdateAndMetadataById = (id: string) =>
  createSelector(
    selectMessageStatusUpdateById(id),
    selectMessageStatusUpdateMetadataById(id),
    (messageStatusUpdate, meta) => ({ messageStatusUpdate, meta }),
  );

export function selectMessageStatusUpdateEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllMessageStatusUpdateEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, MessageStatusUpdateRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectMessageStatusUpdateEntitiesByStatus(status: MessageStatus) {
  return createSelector(selectAllMessageStatusUpdateEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectMessageStatusUpdateEntitiesByError(error: string) {
  return createSelector(selectAllMessageStatusUpdateEntities, (entities) =>
    entities.filter((e) => e.error === error).valueSeq(),
  );
}

export function selectMessageStatusUpdateEntitiesByVendorMessage(vendorMessage: string) {
  return createSelector(selectAllMessageStatusUpdateEntities, (entities) =>
    entities.filter((e) => e.vendorMessage === vendorMessage).valueSeq(),
  );
}

export function selectMessageStatusUpdateEntitiesByMetadataJson(metadataJson: string) {
  return createSelector(selectAllMessageStatusUpdateEntities, (entities) =>
    entities.filter((e) => e.metadataJson === metadataJson).valueSeq(),
  );
}

export function selectMessageStatusUpdateEntitiesByTrackedMessageId(trackedMessageId: string) {
  return createSelector(selectAllMessageStatusUpdateEntities, (entities) =>
    entities.filter((e) => e.trackedMessageId === trackedMessageId).valueSeq(),
  );
}
//#endregion Selectors
